/** Профиль: кто вошёл, счётчики генераций, поддержка, канал и документы. */
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { ErrorState, Loading, OfflineBanner } from '../components/States';
import { displayName, initials } from '../lib/init-user';
import * as telegram from '../lib/telegram';
import { useActions } from '../state/actions';
import { useStore } from '../state/store';
import { api, errorMessage } from '../lib/api';
import { adminRequest } from '../admin/client';
import { REEL_PACKAGES } from '@reels/shared/billing';
import { formatDate, getLang, getLangPreference, LANGUAGES, ls, setLangPreference, t, useLang } from '../i18n';
import type { Lang } from '../i18n';

interface MenuRowProps {
  icon: string;
  title: string;
  hint?: string;
  onClick: () => void;
}

function MenuRow({ icon, title, hint, onClick }: MenuRowProps): ReactElement {
  return (
    <button
      type="button"
      className="menu-row"
      onClick={() => {
        telegram.hapticFeedback.selection();
        onClick();
      }}
    >
      <span className="icon" aria-hidden="true">
        {icon}
      </span>
      <span className="col grow">
        <span>{title}</span>
        {hint ? <span className="small muted">{hint}</span> : null}
      </span>
    </button>
  );
}

function Avatar({ photoUrl, name }: { photoUrl: string | null; name: string }): ReactElement {
  if (photoUrl) return <img className="avatar" src={photoUrl} alt="" />;
  return (
    <span className="avatar" aria-hidden="true">
      {initials(name)}
    </span>
  );
}

/** Язык интерфейса: «как в системе» или явный выбор; хранится на устройстве. */
function LanguageSwitch(): ReactElement {
  useLang();
  const chosen = getLangPreference();
  const options: readonly { id: Lang | null; label: string }[] = [
    { id: null, label: t('profile.language.system') },
    ...LANGUAGES,
  ];
  return (
    <>
      <span className="section-title">{t('profile.language')}</span>
      <div className="segmented" role="group" aria-label={t('profile.language')}>
        {options.map((option) => (
          <button
            key={option.id ?? 'system'}
            type="button"
            aria-pressed={chosen === option.id}
            onClick={() => {
              telegram.hapticFeedback.selection();
              setLangPreference(option.id);
              // Бот пишет в чат на языке приложения; без сети выбор остаётся только на устройстве.
              void api.setLanguage(getLang()).catch(() => {});
            }}
          >
            {option.label}
          </button>
        ))}
      </div>
    </>
  );
}

export function Profile(): ReactElement {
  const { state, dispatch } = useStore();
  const actions = useActions();
  const [adminAccess, setAdminAccess] = useState(false);
  useEffect(() => {
    let live = true;
    void adminRequest('/access')
      .then(() => { if (live) setAdminAccess(true); })
      .catch(() => {});
    return () => { live = false; };
  }, []);
  const [cancelling,setCancelling] = useState(false);
  const [paymentError,setPaymentError] = useState<string | null>(null);
  const cancelLock = useRef(false);
  const cancel = async () => {
    if (cancelLock.current) return;
    cancelLock.current = true; setCancelling(true); setPaymentError(null);
    try { await api.cancelSubscription(); await actions.loadProfile(); }
    catch (error) { setPaymentError(errorMessage(error)); }
    finally { cancelLock.current=false; setCancelling(false); }
  };

  // Счётчики запрашиваем при каждом открытии экрана: они меняются после генераций.
  const requested = useRef(false);
  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    void actions.loadProfile();
  }, [actions]);

  const tgUser = telegram.user;
  const name = tgUser ? displayName(tgUser) : (state.user?.name ?? t('profile.telegramUser'));
  const username = tgUser?.username ?? state.profile?.user.username ?? null;
  const profile = state.profile;
  const terms = profile?.links.terms ?? null;
  const privacy = profile?.links.privacy ?? null;

  return (
    <>
      <h1>{t('profile.title')}</h1>
      {!state.online ? <OfflineBanner /> : null}

      <div className="card">
        <div className="row">
          <Avatar photoUrl={tgUser?.photoUrl ?? null} name={name} />
          <div className="col grow">
            <strong className="ellipsis profile-name">{name}</strong>
            <span className="small muted ellipsis">
              {username ? `@${username}` : null}
              {username && state.user ? ' · ' : null}
              {state.user ? `ID ${state.user.tgId}` : null}
            </span>
          </div>
        </div>
      </div>

      {state.profileError ? (
        <ErrorState message={state.profileError} onRetry={() => void actions.loadProfile()} />
      ) : null}
      {!profile && !state.profileError ? <Loading rows={1} /> : null}
      {profile ? (
        <div className="facts">
          <div className="fact">
            <span className="fact-label">{t('profile.rendered')}</span>
            <span className="fact-value">{profile.stats.rendered}</span>
          </div>
          <div className="fact">
            <span className="fact-label">{t('profile.available')}</span>
            <span className="fact-value">
              {profile.billing?.available ?? '…'}
            </span>
          </div>
        </div>
      ) : null}

      <div className="menu">
        {adminAccess ? (
          <MenuRow
            icon="📊"
            title={t('profile.analytics')}
            hint={t('profile.analytics.hint')}
            onClick={() => { window.location.href = '/admin' + window.location.hash; }}
          />
        ) : null}
        <MenuRow
          icon="💬"
          title={t('profile.support')}
          hint={t('profile.support.hint')}
          onClick={() => dispatch({ type: 'route', screen: 'support' })}
        />
      </div>

      {profile?.billing?.subscription ? <div className="card">
        <strong>{ls(REEL_PACKAGES.find(p => p.id === profile.billing!.subscription!.packageId)?.title ?? '')}</strong>
        <p>{profile.billing.subscription.cancelled ? t('profile.renewalOff') : t('profile.renewalNext')}
          {formatDate(profile.billing.subscription.periodEnd)}</p>
        {profile.billing.subscription.provider === 'tribute' ? <button className="btn" onClick={() => telegram.openLink('https://t.me/tribute')}>{t('profile.manageTribute')}</button> : !profile.billing.subscription.cancelled ? <button className="btn" disabled={cancelling} onClick={() => void cancel()}>
          {cancelling ? t('profile.cancelling') : t('profile.cancel')}
        </button> : null}
      </div> : null}
      {paymentError ? <div className="banner warning" role="alert">{paymentError}</div> : null}

      <LanguageSwitch />

      {terms || privacy ? (
        <>
          <span className="section-title">{t('profile.docs')}</span>
          <div className="menu">
            {terms ? (
              <MenuRow icon="📄" title={t('profile.terms')} onClick={() => telegram.openLink(terms)} />
            ) : null}
            {privacy ? (
              <MenuRow
                icon="🔒"
                title={t('profile.privacy')}
                onClick={() => telegram.openLink(privacy)}
              />
            ) : null}
          </div>
        </>
      ) : null}
    </>
  );
}
